import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const MKT_SERVICES = ['Meta Ads', 'Google Ads', 'SEO', 'SMO', 'GMB', 'Content']
const PROJECT_TYPES = ['Website', 'ERP', 'App', 'AMC', 'Custom Software', 'Other']
const PAY_STATUSES = ['Pending', 'Received', 'Overdue', 'Waived']
const STAGES_DEV = ['Not Started', 'Planning', 'In Progress', 'Review', 'Completed', 'On Hold']
const SERVICE_STATUSES = ['Not Started', 'Running', 'Paused', 'Stopped']

const EMPTY_SERVICE = {
  service_type: 'Marketing',
  status: 'Running',
  // marketing
  services_scope: '',
  billing_cycle: 'Monthly',
  billing_day: '',
  next_billing_date: '',
  value: '',
  payment_status: 'Pending',
  // development
  project_type: 'Website',
  total_value: '',
  est_completion: '',
  // hosting
  domain: '',
  domain_renewal_date: '',
  hosting_provider: '',
  hosting_plan: '',
  // shared
  assigned_to: '',
  project_start: '',
  remarks: '',
}

const EMPTY_MILESTONE = { milestone_name: '', milestone_amount: '', due_date: '', payment_status: 'Pending' }

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

/**
 * AddServiceModal
 *
 * Props:
 *   onClose()           — close without saving
 *   onSaved(service)    — called after successful save with the new/updated service row
 *   lockedClientId      — if set, client selector is hidden (used from Clients page)
 *   lockedBrandId       — if set, brand selector is hidden
 *   lockedServiceType   — if set, service type selector is hidden
 *   editService         — if set, modal is in edit mode (existing services row)
 */
export default function AddServiceModal({
  onClose,
  onSaved,
  lockedClientId = null,
  lockedBrandId = null,
  lockedServiceType = null,
  editService = null,
}) {
  const [clients, setClients] = useState([])
  const [brands, setBrands] = useState([])       // all brands
  const [filteredBrands, setFilteredBrands] = useState([])

  const [clientId, setClientId] = useState(lockedClientId || '')
  const [brandId, setBrandId] = useState(lockedBrandId || '')
  const [form, setForm] = useState(EMPTY_SERVICE)

  // Milestones (dev only)
  const [milestones, setMilestones] = useState([{ ...EMPTY_MILESTONE }])

  // "Add new client" inline
  const [addingClient, setAddingClient] = useState(false)
  const [newClientName, setNewClientName] = useState('')
  const [newClientPhone, setNewClientPhone] = useState('')
  const [newClientEmail, setNewClientEmail] = useState('')
  const [savingClient, setSavingClient] = useState(false)

  // "Add new brand" inline
  const [addingBrand, setAddingBrand] = useState(false)
  const [newBrandName, setNewBrandName] = useState('')
  const [savingBrand, setSavingBrand] = useState(false)

  const [saving, setSaving] = useState(false)

  const isEdit = !!editService

  // Load clients once
  useEffect(() => {
    supabase.from('clients').select('id, name, status').order('name').then(({ data }) => {
      setClients(data || [])
    })
    supabase.from('brands').select('id, name, client_id').order('name').then(({ data }) => {
      setBrands(data || [])
    })
  }, [])

  // Filter brands by selected client
  useEffect(() => {
    if (clientId) {
      setFilteredBrands(brands.filter(b => b.client_id === Number(clientId)))
    } else {
      setFilteredBrands([])
    }
  }, [clientId, brands])

  // Populate form when editing
  useEffect(() => {
    if (editService) {
      setClientId(editService.client_id || '')
      setBrandId(editService.brand_id || '')
      setForm({
        service_type: editService.service_type || 'Marketing',
        status: editService.status || 'Running',
        services_scope: editService.services_scope || '',
        billing_cycle: editService.billing_cycle || 'Monthly',
        billing_day: editService.billing_day || '',
        next_billing_date: editService.next_billing_date || '',
        value: editService.value || '',
        payment_status: editService.payment_status || 'Pending',
        project_type: editService.project_type || 'Website',
        total_value: editService.total_value || '',
        est_completion: editService.est_completion || '',
        domain: editService.domain || '',
        domain_renewal_date: editService.domain_renewal_date || '',
        hosting_provider: editService.hosting_provider || '',
        hosting_plan: editService.hosting_plan || '',
        assigned_to: editService.assigned_to || '',
        project_start: editService.project_start || '',
        remarks: editService.remarks || '',
      })
    }
  }, [editService])

  // Load milestones when editing a dev service
  useEffect(() => {
    if (editService && editService.service_type === 'Development') {
      supabase.from('milestones').select('*').eq('service_id', editService.id).order('created_at').then(({ data }) => {
        if (data && data.length > 0) {
          setMilestones(data.map(m => ({
            id: m.id,
            milestone_name: m.milestone_name,
            milestone_amount: m.milestone_amount,
            due_date: m.due_date || '',
            payment_status: m.payment_status,
          })))
        }
      })
    }
  }, [editService])

  function setF(k, v) { setForm(prev => ({ ...prev, [k]: v })) }

  function toggleScope(s) {
    const current = form.services_scope.split(',').map(x => x.trim()).filter(Boolean)
    const next = current.includes(s) ? current.filter(x => x !== s) : [...current, s]
    setF('services_scope', next.join(', '))
  }

  // Add new client inline
  async function saveNewClient() {
    if (!newClientName.trim()) return
    setSavingClient(true)
    const { data } = await supabase.from('clients').insert([{
      name: newClientName.trim(),
      phone: newClientPhone.trim(),
      email: newClientEmail.trim(),
      status: 'Active',
    }]).select()
    if (data?.[0]) {
      setClients(prev => [...prev, data[0]])
      setClientId(data[0].id)
      setBrandId('')
    }
    setAddingClient(false)
    setNewClientName(''); setNewClientPhone(''); setNewClientEmail('')
    setSavingClient(false)
  }

  // Add new brand inline
  async function saveNewBrand() {
    if (!newBrandName.trim() || !clientId) return
    setSavingBrand(true)
    const { data } = await supabase.from('brands').insert([{
      client_id: Number(clientId),
      name: newBrandName.trim(),
    }]).select()
    if (data?.[0]) {
      setBrands(prev => [...prev, data[0]])
      setBrandId(data[0].id)
    }
    setAddingBrand(false)
    setNewBrandName('')
    setSavingBrand(false)
  }

  // Milestones helpers
  function addMilestone() {
    setMilestones(prev => [...prev, { ...EMPTY_MILESTONE }])
  }
  function updateMilestone(i, key, val) {
    setMilestones(prev => prev.map((m, idx) => idx === i ? { ...m, [key]: val } : m))
  }
  function removeMilestone(i) {
    setMilestones(prev => prev.filter((_, idx) => idx !== i))
  }

  async function save() {
    if (!clientId || !brandId) return
    setSaving(true)

    const serviceType = lockedServiceType || form.service_type
    const payload = {
      client_id: Number(clientId),
      brand_id: Number(brandId),
      service_type: serviceType,
      status: form.status,
      value: Number(form.value) || 0,
      billing_cycle: form.billing_cycle || null,
      billing_day: Number(form.billing_day) || null,
      next_billing_date: form.next_billing_date || null,
      payment_status: form.payment_status,
      assigned_to: form.assigned_to || null,
      project_start: form.project_start || null,
      remarks: form.remarks || null,
      // marketing
      services_scope: serviceType === 'Marketing' ? form.services_scope : null,
      // development
      project_type: serviceType === 'Development' ? form.project_type : null,
      total_value: serviceType === 'Development' ? (Number(form.total_value) || 0) : null,
      est_completion: serviceType === 'Development' ? (form.est_completion || null) : null,
      // hosting
      domain: serviceType === 'Hosting' ? (form.domain || null) : null,
      domain_renewal_date: serviceType === 'Hosting' ? (form.domain_renewal_date || null) : null,
      hosting_provider: serviceType === 'Hosting' ? (form.hosting_provider || null) : null,
      hosting_plan: serviceType === 'Hosting' ? (form.hosting_plan || null) : null,
    }

    let savedService
    if (isEdit) {
      const { data } = await supabase.from('services').update(payload).eq('id', editService.id).select()
      savedService = data?.[0]
    } else {
      const { data } = await supabase.from('services').insert([payload]).select()
      savedService = data?.[0]
    }

    // Handle milestones for Development
    if (serviceType === 'Development' && savedService) {
      if (isEdit) {
        // Delete milestones that were removed (those without an id)
        const keptIds = milestones.filter(m => m.id).map(m => m.id)
        const { data: existingMs } = await supabase.from('milestones').select('id').eq('service_id', savedService.id)
        const toDelete = (existingMs || []).filter(m => !keptIds.includes(m.id)).map(m => m.id)
        for (const id of toDelete) {
          await supabase.from('milestones').delete().eq('id', id)
        }
        // Upsert each milestone
        for (const m of milestones) {
          if (!m.milestone_name) continue
          const mPayload = {
            service_id: savedService.id,
            milestone_name: m.milestone_name,
            milestone_amount: Number(m.milestone_amount) || 0,
            due_date: m.due_date || null,
            payment_status: m.payment_status,
          }
          if (m.id) {
            await supabase.from('milestones').update(mPayload).eq('id', m.id)
          } else {
            await supabase.from('milestones').insert([mPayload])
          }
        }
      } else {
        // New service — insert all milestones
        for (const m of milestones) {
          if (!m.milestone_name) continue
          await supabase.from('milestones').insert([{
            service_id: savedService.id,
            milestone_name: m.milestone_name,
            milestone_amount: Number(m.milestone_amount) || 0,
            due_date: m.due_date || null,
            payment_status: m.payment_status,
          }])
        }
      }
    }

    setSaving(false)
    if (savedService) onSaved(savedService)
  }

  const serviceType = lockedServiceType || form.service_type
  const selectedClient = clients.find(c => c.id === Number(clientId))
  const canSave = clientId && brandId && !saving

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit service' : 'Add service'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">

          {/* ── Client ── */}
          {!lockedClientId && (
            <div className="form-field" style={{ marginBottom: 16 }}>
              <label>Client *</label>
              {addingClient ? (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, background: 'var(--surface2)' }}>
                  <div className="form-row" style={{ marginBottom: 8 }}>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: 11 }}>Name *</label>
                      <input value={newClientName} onChange={e => setNewClientName(e.target.value)} placeholder="Client / company name" autoFocus />
                    </div>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: 11 }}>Phone</label>
                      <input value={newClientPhone} onChange={e => setNewClientPhone(e.target.value)} placeholder="10-digit" />
                    </div>
                  </div>
                  <div className="form-field" style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11 }}>Email</label>
                    <input value={newClientEmail} onChange={e => setNewClientEmail(e.target.value)} placeholder="email@example.com" />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 14px' }} onClick={saveNewClient} disabled={savingClient || !newClientName.trim()}>
                      {savingClient ? 'Saving…' : 'Save client'}
                    </button>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 14px' }} onClick={() => setAddingClient(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <select value={clientId} onChange={e => { setClientId(e.target.value); setBrandId('') }} style={{ flex: 1 }}>
                    <option value="">— Select client —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.status === 'Inactive' ? ' (Inactive)' : ''}</option>
                    ))}
                  </select>
                  <button className="btn" style={{ fontSize: 12, whiteSpace: 'nowrap' }} onClick={() => setAddingClient(true)}>+ New client</button>
                </div>
              )}
            </div>
          )}

          {/* ── Brand / Project ── */}
          {!lockedBrandId && (
            <div className="form-field" style={{ marginBottom: 16 }}>
              <label>Brand / Project *</label>
              {addingBrand ? (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, background: 'var(--surface2)' }}>
                  <div className="form-field" style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11 }}>Brand / project name *</label>
                    <input value={newBrandName} onChange={e => setNewBrandName(e.target.value)} placeholder="e.g. Aryanss Website" autoFocus />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 14px' }} onClick={saveNewBrand} disabled={savingBrand || !newBrandName.trim() || !clientId}>
                      {savingBrand ? 'Saving…' : 'Save brand'}
                    </button>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 14px' }} onClick={() => setAddingBrand(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={brandId}
                    onChange={e => setBrandId(e.target.value)}
                    disabled={!clientId}
                    style={{ flex: 1 }}
                  >
                    <option value="">{clientId ? '— Select brand / project —' : '— Select client first —'}</option>
                    {filteredBrands.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <button className="btn" style={{ fontSize: 12, whiteSpace: 'nowrap' }} onClick={() => setAddingBrand(true)} disabled={!clientId}>+ New brand</button>
                </div>
              )}
            </div>
          )}

          {/* ── Service type + status ── */}
          <div className="form-row">
            {!lockedServiceType && (
              <div className="form-field">
                <label>Service type *</label>
                <select value={form.service_type} onChange={e => setF('service_type', e.target.value)}>
                  <option>Marketing</option>
                  <option>Development</option>
                  <option>Hosting</option>
                </select>
              </div>
            )}
            <div className="form-field">
              <label>Status</label>
              <select value={form.status} onChange={e => setF('status', e.target.value)}>
                {SERVICE_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* ── Marketing fields ── */}
          {serviceType === 'Marketing' && (
            <>
              <div className="form-field">
                <label>Services scope</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {MKT_SERVICES.map(s => {
                    const selected = form.services_scope.split(',').map(x => x.trim()).filter(Boolean).includes(s)
                    return (
                      <button key={s} type="button"
                        className={selected ? 'badge badge-blue' : 'badge badge-gray'}
                        style={{ cursor: 'pointer', border: selected ? '1.5px solid var(--accent)' : '1.5px solid var(--border)', padding: '4px 12px', fontSize: 12 }}
                        onClick={() => toggleScope(s)}
                      >{s}</button>
                    )
                  })}
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Value (₹)</label>
                  <input type="number" value={form.value} onChange={e => setF('value', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Billing period</label>
                  <select value={form.billing_cycle} onChange={e => setF('billing_cycle', e.target.value)}>
                    <option>Monthly</option><option>Quarterly</option><option>Half Yearly</option><option>Yearly</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Billing day of month</label>
                  <input type="number" min="1" max="31" value={form.billing_day} onChange={e => setF('billing_day', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Next billing date</label>
                  <input type="date" value={form.next_billing_date} onChange={e => setF('next_billing_date', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* ── Development fields ── */}
          {serviceType === 'Development' && (
            <>
              <div className="form-row">
                <div className="form-field">
                  <label>Project type</label>
                  <select value={form.project_type} onChange={e => setF('project_type', e.target.value)}>
                    {PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Total project value (₹)</label>
                  <input type="number" value={form.total_value} onChange={e => setF('total_value', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Project start</label>
                  <input type="date" value={form.project_start} onChange={e => setF('project_start', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Est. completion</label>
                  <input type="date" value={form.est_completion} onChange={e => setF('est_completion', e.target.value)} />
                </div>
              </div>
              {/* Milestones */}
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontWeight: 500, fontSize: 13 }}>Milestones / Payments</label>
                  <button className="btn" style={{ fontSize: 11, padding: '3px 10px' }} onClick={addMilestone}>+ Add</button>
                </div>
                {milestones.map((m, i) => (
                  <div key={i} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 8 }}>
                    <div className="form-row" style={{ marginBottom: 6 }}>
                      <div className="form-field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Milestone name</label>
                        <input value={m.milestone_name} onChange={e => updateMilestone(i, 'milestone_name', e.target.value)} placeholder="e.g. Advance (30%)" />
                      </div>
                      <div className="form-field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Amount (₹)</label>
                        <input type="number" value={m.milestone_amount} onChange={e => updateMilestone(i, 'milestone_amount', e.target.value)} />
                      </div>
                    </div>
                    <div className="form-row" style={{ marginBottom: 0 }}>
                      <div className="form-field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 11 }}>Due date</label>
                        <input type="date" value={m.due_date} onChange={e => updateMilestone(i, 'due_date', e.target.value)} />
                      </div>
                      <div className="form-field" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 11 }}>Payment status</label>
                          <select value={m.payment_status} onChange={e => updateMilestone(i, 'payment_status', e.target.value)}>
                            {PAY_STATUSES.map(s => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                        {milestones.length > 1 && (
                          <button className="btn btn-danger" style={{ fontSize: 11, padding: '4px 8px', marginBottom: 0 }} onClick={() => removeMilestone(i)}>✕</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Hosting fields ── */}
          {serviceType === 'Hosting' && (
            <>
              <div className="form-row">
                <div className="form-field">
                  <label>Domain</label>
                  <input value={form.domain} onChange={e => setF('domain', e.target.value)} placeholder="example.com" />
                </div>
                <div className="form-field">
                  <label>Domain renewal date</label>
                  <input type="date" value={form.domain_renewal_date} onChange={e => setF('domain_renewal_date', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Hosting provider</label>
                  <input value={form.hosting_provider} onChange={e => setF('hosting_provider', e.target.value)} placeholder="GoDaddy, Hostinger, etc." />
                </div>
                <div className="form-field">
                  <label>Plan</label>
                  <input value={form.hosting_plan} onChange={e => setF('hosting_plan', e.target.value)} placeholder="Starter, Business, etc." />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Value (₹)</label>
                  <input type="number" value={form.value} onChange={e => setF('value', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Billing period</label>
                  <select value={form.billing_cycle} onChange={e => setF('billing_cycle', e.target.value)}>
                    <option>Monthly</option><option>Quarterly</option><option>Half Yearly</option><option>Yearly</option><option>Annual</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Next billing date</label>
                  <input type="date" value={form.next_billing_date} onChange={e => setF('next_billing_date', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Payment status</label>
                  <select value={form.payment_status} onChange={e => setF('payment_status', e.target.value)}>
                    {PAY_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* ── Shared fields ── */}
          <div className="form-row" style={{ marginTop: serviceType === 'Marketing' ? 0 : 4 }}>
            <div className="form-field">
              <label>Assigned to</label>
              <input value={form.assigned_to} onChange={e => setF('assigned_to', e.target.value)} />
            </div>
            {serviceType !== 'Development' && (
              <div className="form-field">
                <label>Project start</label>
                <input type="date" value={form.project_start} onChange={e => setF('project_start', e.target.value)} />
              </div>
            )}
          </div>
          <div className="form-field">
            <label>Remarks</label>
            <textarea rows={2} value={form.remarks} onChange={e => setF('remarks', e.target.value)} style={{ resize: 'vertical' }} />
          </div>

        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add service'}
          </button>
        </div>
      </div>
    </div>
  )
}
